import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { Server, LogOut, BookOpen, MessageSquare, Shield, LayoutDashboard, Menu } from 'lucide-react';
import { ServerControls } from './ServerControls';
import { ServerCatalog } from './ServerCatalog';
import { SupportTickets } from './SupportTickets';
import { LegalNotice } from './LegalNotice';
import { ClientDashboard } from './ClientDashboard';

type ClientTab = 'dashboard' | 'servers' | 'catalog' | 'support' | 'legal';

const Footer: React.FC = () => (
  <footer className="text-center py-6 text-sm text-gray-400">
    © 2025 LisaServer.com · Todos los derechos reservados · Una marca de AthosFera, LLC
  </footer>
);

const ClientPanel: React.FC = () => {
  const { user, logout } = useAuth();
  const { clientServers, clientTickets, loading: dataLoading, refreshData } = useData();
  const [activeTab, setActiveTab] = useState<ClientTab>('dashboard');
  const [isSidebarOpen, setSidebarOpen] = useState(false);

  const handleNavClick = (tab: ClientTab) => {
    setActiveTab(tab);
    setSidebarOpen(false);
  };
  
  // La función de refresco ahora es simplemente 'refreshData' del contexto.
  // ServerControls ya lo llama automáticamente a través de la función del DataContext.
  const handleActionCompleted = () => {
      refreshData();
  };

  return (
    <div className="flex min-h-screen bg-gray-100 font-sans">
      <aside className={`fixed lg:relative z-20 h-full w-64 bg-slate-900 text-gray-300 flex-col shadow-lg transition-transform transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="flex items-center justify-center h-20 border-b border-slate-700">
          <img src="/logo-lisa-5.png" alt="Logo Principal" className="h-12" />
        </div>
        <nav className="flex-1 px-4 py-6 space-y-2">
            <a href="#" onClick={() => handleNavClick('dashboard')} className={`flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${activeTab === 'dashboard' ? 'bg-blue-600 text-white shadow-lg' : 'hover:bg-slate-800 hover:text-white'}`}>
                <LayoutDashboard className="h-5 w-5 mr-3" /> Dashboard
            </a>
            <a href="#" onClick={() => handleNavClick('servers')} className={`flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${activeTab === 'servers' ? 'bg-blue-600 text-white shadow-lg' : 'hover:bg-slate-800 hover:text-white'}`}>
                <Server className="h-5 w-5 mr-3" /> Mis Servidores
            </a>
            <a href="#" onClick={() => handleNavClick('catalog')} className={`flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${activeTab === 'catalog' ? 'bg-blue-600 text-white shadow-lg' : 'hover:bg-slate-800 hover:text-white'}`}>
                <BookOpen className="h-5 w-5 mr-3" /> Catálogo
            </a>
            <a href="#" onClick={() => handleNavClick('support')} className={`flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${activeTab === 'support' ? 'bg-blue-600 text-white shadow-lg' : 'hover:bg-slate-800 hover:text-white'}`}>
                <MessageSquare className="h-5 w-5 mr-3" /> Soporte
            </a>
            <a href="#" onClick={() => handleNavClick('legal')} className={`flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${activeTab === 'legal' ? 'bg-blue-600 text-white shadow-lg' : 'hover:bg-slate-800 hover:text-white'}`}>
                <Shield className="h-5 w-5 mr-3" /> Aviso Legal
            </a>
        </nav>
        <div className="px-4 py-4 border-t border-slate-700">
            <button onClick={logout} className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-slate-800 hover:text-white rounded-lg flex items-center">
                <LogOut className="h-5 w-5 mr-3" />
                <span>Cerrar Sesión ({user?.username})</span>
            </button>
        </div>
      </aside>
      
      <div className="flex-1 flex flex-col">
        <header className="bg-white h-20 flex items-center justify-between px-4 lg:px-8 border-b border-gray-200">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-gray-600">
            <Menu className="h-6 w-6" />
          </button>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-800 capitalize">
            {activeTab === 'dashboard' ? 'Resumen de Cuenta' : activeTab === 'servers' ? 'Mis Servidores' : activeTab === 'catalog' ? 'Catálogo' : activeTab === 'support' ? 'Soporte' : 'Aviso Legal'}
          </h1>
          <div className="lg:hidden w-8"></div>
        </header>
        
        {/* ===== INICIO DE LA CORRECCIÓN DE RENDERIZADO ===== */}
        <main className="flex-1 p-4 lg:p-8 overflow-y-auto">
          {dataLoading ? (
            <div className="flex items-center justify-center p-10">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <>
              {activeTab === 'dashboard' && <ClientDashboard servers={clientServers} tickets={clientTickets} onNavigate={(tab) => handleNavClick(tab as ClientTab)} />}
              {activeTab === 'servers' && <ServerControls servers={clientServers} onActionCompleted={handleActionCompleted} />}
              {activeTab === 'catalog' && <ServerCatalog user={user} />}
              {activeTab === 'support' && <SupportTickets />}
              {activeTab === 'legal' && <LegalNotice />}
            </>
          )}
        </main>
        {/* ===== FIN DE LA CORRECCIÓN DE RENDERIZADO ===== */}

        <Footer />
      </div>

       {isSidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} className="fixed inset-0 bg-black opacity-50 z-10 lg:hidden"></div>
      )}
    </div>
  );
};

export default ClientPanel;